import React from 'react';
import { mdiContentCopy, mdiOpenInNew, mdiPencil } from '@mdi/js';
import _ from 'lodash';
import { IEntityConfig } from './entity-manager.types';
import { CoreFetch } from '../../../api';
import { ClaimApi, SupportClaim, ProviderClaim, CodingApi, SupportCoding, ProviderCoding } from '../../../api/server';
import { formatDate } from '../../../services/primitives/date-utils';
import { ResponsibilityBadge } from '../../badge';


export const createClaimConfig = (
    isSupportApp: boolean,
    accountId?: string,
    onClaimSearch?: (term: string) => Promise<(SupportClaim | ProviderClaim)[]>
): IEntityConfig<SupportClaim | ProviderClaim> => {
    const formatDateString = (dateStr: string | undefined): string => {
        if (!dateStr) return '-';
        try {
            return formatDate(dateStr, 'MMM d yyyy');
        } catch {
            return '-';
        }
    };

    const formatAmount = (amount: any): string => {
        if (!amount || !amount.amount) return '$0.00';
        const value = parseFloat(amount.amount);
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: amount.currency || 'USD',
        }).format(value);
    };

    const getPatientName = (claim: SupportClaim | ProviderClaim): string => {
        const firstName = claim.patientFirst || '';
        const lastName = claim.patientLast || '';
        return `${firstName} ${lastName}`.trim() || 'Unknown';
    };

    return {
        entityType: 'CLAIM',
        displayName: 'Claim',
        displayNamePlural: 'Claims',

        searchFn: async (query: string) => {
            if (!onClaimSearch) {
                return [];
            }
            return await onClaimSearch(query);
        },

        fetchEntitiesFn: async (entityIds: string[]) => {
            if (!entityIds || entityIds.length === 0) return [];
            const filterIds = entityIds.map(id => `EQ:${id}`);
            return isSupportApp
                ? await CoreFetch(ClaimApi).listSupportClaims({ filterId: filterIds })
                : await CoreFetch(ClaimApi).listProviderClaims({ filterId: filterIds });
        },

        columns: [
            {
                key: 'patientName',
                label: 'Patient Name',
                render: (claim) => getPatientName(claim),
            },
            {
                key: 'dos',
                label: 'DOS',
                render: (claim) => formatDateString(claim.fromDos),
            },
            {
                key: 'contractValue',
                label: 'Contract Value',
                render: (claim) => formatAmount(claim.chargeAmount),
            },
        ],

        actions: [
            {
                key: 'copy',
                label: 'Copy Claim ID',
                icon: mdiContentCopy,
                handler: (claim) => {
                    if (claim.id) {
                        navigator.clipboard.writeText(claim.id);
                    }
                },
            },
            {
                key: 'edit',
                label: 'Edit Claim',
                icon: mdiPencil,
                handler: (claim) => {
                    if (!claim.id) return;
                    if (isSupportApp) {
                        const accId = accountId;
                        if (accId) {
                            window.open(`/${accId}/claims/${claim.id}/edit`, '_blank');
                        }
                    } else {
                        // Provider app uses /{accountId}/claims/{claimId}/edit or /claims/{claimId}/edit as fallback
                        const path = accountId ? `/${accountId}/claims/${claim.id}/edit` : `/claims/${claim.id}/edit`;
                        window.open(path, '_blank');
                    }
                },
            },
            {
                key: 'view',
                label: 'View Claim',
                icon: mdiOpenInNew,
                handler: (claim) => {
                    if (!claim.id) return;
                    if (isSupportApp) {
                        const accId = accountId;
                        if (accId) {
                            window.open(`/${accId}/claims/${claim.id}`, '_blank');
                        }
                    } else {
                        // Provider app uses /{accountId}/claims/{claimId} or /claims/{claimId} as fallback
                        const path = accountId ? `/${accountId}/claims/${claim.id}` : `/claims/${claim.id}`;
                        window.open(path, '_blank');
                    }
                },
            },
        ],

        searchPlaceholder: 'Search for claims or paste claim IDs',

        summaryMetrics: [
            {
                label: 'Claims',
                calculate: (claims) => claims.length,
            },
            {
                label: 'Contract Value',
                calculate: (claims) => {
                    const total = claims.reduce((sum, claim) => {
                        const value = claim.chargeAmount?.amount ? parseFloat(claim.chargeAmount.amount) : 0;
                        return sum + value;
                    }, 0);
                    const currency = claims[0]?.chargeAmount?.currency || 'USD';
                    return new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: currency,
                    }).format(total);
                },
            },
        ],

        getDropdownRows: (claim) => {
            const from = claim.fromDos ? formatDate(claim.fromDos, 'MMM d') : undefined;
            const through = claim.throughDos ? formatDate(claim.throughDos, 'MMM d') : undefined;
            const serviceDate = through ? `${from} – ${through}` : from;
            const totalCharges = _.get(claim, 'chargeAmount.amount', Number.NaN);

            return [
                [
                    <span key="date">{serviceDate}</span>,
                    <span key="patient">{claim.patientFirst} {claim.patientLast}</span>
                ],
                [
                    <ResponsibilityBadge key="badge" size="small" responsibility={claim.claimResponsibilityType} />,
                    <span key="payer">{claim.primaryPayerName}</span>,
                    <span key="charges">${!Number.isNaN(totalCharges) ? totalCharges : 0}</span>
                ]
            ];
        },

        getEntityId: (claim) => claim.id,
    };
};


export const createCodingConfig = (
    isSupportApp: boolean,
    accountId?: string
): IEntityConfig<SupportCoding | ProviderCoding> => {
    const formatStatus = (status: string | undefined): string => {
        if (!status) return '-';
        return status.charAt(0) + status.slice(1).toLowerCase();
    };

    const formatVendor = (vendor: string | undefined): string => {
        if (!vendor) return '-';
        if (vendor === 'THIRD_PARTY') return 'Third Party';
        return vendor.charAt(0) + vendor.slice(1).toLowerCase();
    };

    return {
        entityType: 'CODING',
        displayName: 'Coding',
        displayNamePlural: 'Codings',

        searchFn: async (query: string) => {
            if (!query.trim()) return [];
            const filterIds = [query.trim()].map(id => `EQ:${id}`);
            return isSupportApp
                ? await CoreFetch(CodingApi).listSupportCodings({ filterId: filterIds })
                : await CoreFetch(CodingApi).listProviderCodings({ filterId: filterIds });
        },

        fetchEntitiesFn: async (entityIds: string[]) => {
            if (!entityIds || entityIds.length === 0) return [];
            const filterIds = entityIds.map(id => `EQ:${id}`);
            return isSupportApp
                ? await CoreFetch(CodingApi).listSupportCodings({ filterId: filterIds })
                : await CoreFetch(CodingApi).listProviderCodings({ filterId: filterIds });
        },

        columns: [
            {
                key: 'status',
                label: 'Status',
                render: (coding) => formatStatus(coding.status),
            },
            {
                key: 'vendor',
                label: 'Vendor',
                render: (coding) => formatVendor(coding.vendor),
            },
            {
                key: 'notes',
                label: 'Notes',
                render: (coding) => (
                    <span>{coding.notes}</span>
                ),
            }
        ],

        actions: [
            {
                key: 'copy',
                label: 'Copy Coding ID',
                icon: mdiContentCopy,
                handler: (coding) => {
                    if (coding.id) {
                        navigator.clipboard.writeText(coding.id);
                    }
                },
            },
            {
                key: 'view',
                label: 'View Claim',
                icon: mdiOpenInNew,
                handler: (coding) => {
                    if (!coding.claimId) return;
                    if (isSupportApp) {
                        const accId = accountId;
                        if (accId) {
                            window.open(`/${accId}/claims/${coding.claimId}`, '_blank');
                        }
                    } else {
                        window.open(`/claims/${coding.claimId}`, '_blank');
                    }
                },
            },
        ],

        searchPlaceholder: 'Search for codings or paste coding IDs',

        summaryMetrics: [
            {
                label: 'Codings',
                calculate: (codings) => codings.length,
            },
        ],

        getEntityId: (coding) => coding.id,
    };
};

